<script setup lang="ts">
import VitaCounter from "@/components/VitaCounter.vue";
import VitaEmptyPlaceholder from "./UI/VitaEmptyPlaceholder.vue";
import {
  CounterPanel,
  ErrorResponseData,
  FrvQuery,
  TimeShiftQuery,
} from "@/types";
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import {
  addDays,
  addHours,
  addMonths,
  endOfMonth,
  format,
  isLastDayOfMonth,
  parse,
  isSameMonth,
  startOfDay,
  startOfMonth,
} from "date-fns";
import VitaInput from "./UI/VitaInput.vue";
import VitaButton from "./UI/VitaButton.vue";
import VitaInfoModal from "./UI/VitaInfoModal.vue";
import VitaIcon from "./UI/VitaIcon.vue";
import VitaSelect from "./UI/VitaSelect.vue";
import VitaLoaderSpinner from "./UI/VitaLoaderSpinner.vue";
import { ru } from "date-fns/locale";
import { computed, ref } from "vue";
import { useStore } from "vuex";
import { POSITION, useToast } from "vue-toastification";
import { CloseReason, CloseTypeReason, ShiftNeedsPayload } from "@/types/beru";
import { dinerCalculation, frvCore } from "@/utils/frv";
import { v4 as uuid } from "uuid";
import { AxiosError } from "axios";
import { reachGoal } from "@/utils/metrika";

defineProps<{
  open: boolean;
}>();

const emit = defineEmits(["toggleInfoModal", "created"]);

const store = useStore();
const toast = useToast();

const selectedDate = ref<Date>(addDays(startOfDay(new Date()), 1));
const timeFrom = ref("");
const timeTo = ref("");
const selectedReason = ref("");
const selectedTypeReason = ref("");
const isCreateModalLoading = ref(false);
const hasLoadingError = ref(false);
const timeFRVDiff = ref<FrvQuery>({ frv: 0, frvRV: 0 });
const hasSubmitError = ref(false);
const submitInProgress = ref(false);
const loadedFrvMonthKey = ref<string | null>(null);

const timeShiftQuery: TimeShiftQuery = {
  timeShift: 0,
  zupGuid: "",
  timeShiftNoDiner: 0,
  timeShiftFull: 0,
  holiday: false,
  frvRv: 0,
  dinners: {
    first: {
      startTime: 0,
      endTime: 0,
      duration: 0,
      durationMinutes: 0,
    },
    second: {
      startTime: 0,
      endTime: 0,
      duration: 0,
      durationMinutes: 0,
    },
  },
};

const shifts = ref({
  dateFrom: "",
});

const needsObj = ref<ShiftNeedsPayload>({
  pharmId: "",
  dateFrom: "",
  dateTo: "",
  reasonId: "",
  reasonName: "",
  typeReasonId: "",
  typeReasonName: "",
  tempId: "",
});

// Панели счётчиков модалки — отдельные от главного экрана, могут содержать ФРВ следующего месяца
const coreStaff = computed<CounterPanel>(() => {
  return store.getters["data/beruCoreStaff"];
});

const holidaysFRVData = computed<CounterPanel>(() => {
  return store.getters["data/beruFRV_RV"];
});

const holidaysCount = computed<number>(() => {
  return (
    store.getters["data/beruHolidays"] &&
    store.getters["data/beruHolidays"].length
  );
});

const hasHolidaysFRVData = computed(
  () => holidaysCount.value > 0 && holidaysFRVData.value,
);

const minDate = computed(() => addDays(startOfDay(new Date()), 1));

const maxDate = computed(() => endOfMonth(addMonths(minDate.value, 1)));

// Обработка события выбора даты с datepicker
const handleDateChange = async (date: Date) => {
  selectedDate.value = date;

  hasLoadingError.value = false;
  isCreateModalLoading.value = true;
  try {
    await syncFrvForSelectedDate();
  } catch {
    hasLoadingError.value = true;
  } finally {
    isCreateModalLoading.value = false;
  }

  inputEvent();
};

const pharmId = computed(() => {
  return store.getters["data/pharmId"];
});

// Праздничный ли выбранный день
const isHoliday = computed<boolean>(() => {
  return (
    store.getters["data/beruHolidays"] &&
    store.getters["data/beruHolidays"].includes(
      format(selectedDate.value, "d.M.yyyy"),
    )
  );
});

// Часовой пояс аптеки для расчёта смен в последнем дне месяца
const pharmTz = computed<number>(() => {
  return store.getters["data/pharmTz"] || 0;
});

// Пересчёт ФРВ и ФРВ РВ в счётчиках по введённому времени потребности
const inputEvent = () => {
  if (!timeFrom.value || !timeTo.value) {
    timeFRVDiff.value = { frv: 0, frvRV: 0 };
    store.commit("data/changeBeruFRV", timeFRVDiff.value);
    return;
  }

  const isLastDay = isLastDayOfMonth(selectedDate.value);
  shifts.value.dateFrom = `${format(selectedDate.value, "yyyy-MM-dd")}T${
    timeFrom.value
  }`;

  dinerCalculation(
    timeShiftQuery,
    timeFrom,
    timeTo,
    selectedDate.value,
    pharmTz.value,
  );
  timeFRVDiff.value = {
    ...frvCore(timeShiftQuery, isLastDay, isHoliday, shifts, timeFrom, timeTo),
  };

  timeShiftQuery.timeShift = timeFRVDiff.value.frv;
  timeShiftQuery.frvRv = timeFRVDiff.value.frvRV;

  store.commit("data/changeBeruFRV", timeFRVDiff.value);
};

const closeReasons = computed<CloseReason[] | []>(() => {
  return store.getters["beru/closeReasons"];
});

const closeTypeReasons = computed<CloseTypeReason[] | []>(() => {
  return store.getters["beru/closeTypeReasons"];
});

// Предзаполнение времени открытия/закрытия временем работы аптеки на завтра (OMR-7152)
const fillTomorrowWorkTime = () => {
  const workTime: string | null = store.getters["data/tomorrowWorkTime"];
  if (!workTime || workTime === "00:00-00:00") return;

  const [from, to] = workTime.split("-");
  const isValidTime = (time?: string) => !!time && /^\d{2}:\d{2}$/.test(time);
  if (!isValidTime(from) || !isValidTime(to)) return;

  timeFrom.value = from;
  timeTo.value = to;
  inputEvent();
};

const onCreateModalOpen = async () => {
  loadedFrvMonthKey.value = null; // сброс при каждом открытии модалки

  const getReasons = async () => {
    try {
      const reasonId = await store.dispatch("beru/getCloseReasons");
      if (reasonId) {
        await store.dispatch("beru/getCloseTypeReasons", reasonId);
      } else {
        store.commit("beru/setCloseTypeReasons", []);
      }
    } catch (e) {
      store.commit("beru/setCloseReasons", []);
      store.commit("beru/setCloseTypeReasons", []);
      throw e;
    }
  };

  hasLoadingError.value = false;
  isCreateModalLoading.value = true;
  try {
    await Promise.all([getReasons(), syncFrvForSelectedDate()]);
    fillTomorrowWorkTime();
  } catch {
    hasLoadingError.value = true;
  } finally {
    isCreateModalLoading.value = false;
  }
};

const resetCreateForm = () => {
  selectedReason.value = "";
  selectedTypeReason.value = "";
  selectedDate.value = addDays(startOfDay(new Date()), 1);
  timeFrom.value = "";
  timeTo.value = "";
  timeFRVDiff.value = { frv: 0, frvRV: 0 };
  loadedFrvMonthKey.value = null;
  store.commit("data/changeBeruFRV", timeFRVDiff.value);
  store.commit("data/setBeruHolidays", null);
};

// Проверка, что все переданные поля формы заполнены
const isFormFilled = (...fields: unknown[]) => {
  return fields.every(
    (field) => field !== null && field !== undefined && field !== "",
  );
};

const btnDisabled = computed(() => {
  return !isFormFilled(
    selectedReason.value,
    selectedTypeReason.value,
    selectedDate.value,
    timeFrom.value,
    timeTo.value,
  );
});

const onFormSubmit = async () => {
  const date = format(selectedDate.value, "yyyy-MM-dd");
  // Дата и время потребности в таймзоне пользователя
  const timezoneOffset = Math.abs(new Date().getTimezoneOffset() / 60);
  const toClientISO = (time: string) =>
    addHours(
      parse(`${date} ${time}`, "yyyy-MM-dd HH:mm", new Date()),
      timezoneOffset,
    ).toISOString();

  needsObj.value = {
    pharmId: pharmId.value,
    dateFrom: toClientISO(timeFrom.value),
    dateTo: toClientISO(timeTo.value),
    reasonId: selectedReason.value,
    reasonName:
      closeReasons.value.find((r) => r.id === selectedReason.value)?.reason ??
      "",
    typeReasonId: selectedTypeReason.value,
    typeReasonName:
      closeTypeReasons.value.find((r) => r.id === selectedTypeReason.value)
        ?.typeReason ?? "",
    tempId: uuid(),
  };

  try {
    hasSubmitError.value = false;
    submitInProgress.value = true;
    await store.dispatch("beru/createShiftNeeds", needsObj.value);
    toast.success("Успешно", {
      position: POSITION.BOTTOM_RIGHT,
    });
    emit("toggleInfoModal");
    emit("created");
    reachGoal('beru-create-success')
    resetCreateForm();
  } catch (err) {
    const e = err as AxiosError<ErrorResponseData>;
    hasSubmitError.value = true;

    toast.error(
      e.status === 500 ? "Ошибка загрузки данных" : e.response?.data?.message,
      {
        position: POSITION.BOTTOM_RIGHT,
      },
    );
  } finally {
    submitInProgress.value = false;
  }
};

// Месяц выбранной даты (начало месяца) - используем его как базу для всех расчётов
const selectedMonthStart = computed(() => startOfMonth(selectedDate.value));

// Совпадает ли месяц выбранный в datepicker с текущим месяцем
const isCurrentRealMonth = computed(() =>
  isSameMonth(selectedDate.value, new Date()),
);

// Месяц и год, за которые показывается ФРВ в модалке
const countersMonthTitle = computed(() => {
  const title = format(selectedMonthStart.value, "LLLL yyyy", { locale: ru });
  return title.charAt(0).toUpperCase() + title.slice(1);
});

// Загружаем ФРВ и праздники под месяц выбранной даты
const syncFrvForSelectedDate = async () => {
  const monthKey = format(selectedMonthStart.value, "yyyy-MM");

  if (loadedFrvMonthKey.value === monthKey) return;

  const mainFRV = store.getters["data/FRV"];
  const mainHolidays = store.getters["data/holidays"];
  const canReuseMainData = isCurrentRealMonth.value && mainFRV && mainHolidays;

  if (canReuseMainData) {
    // Выбранная дата в текущем месяце — переиспользуем данные главного экрана
    store.commit("data/setBeruFondWorkTime", mainFRV);
    store.commit("data/setBeruHolidays", mainHolidays);
  } else {
    // Любой другой месяц — запрашиваем данные именно за месяц выбранной даты
    const frvDate = format(selectedMonthStart.value, "yyyy-MM-dd");

    await Promise.all([
      store.dispatch("data/getBeruFondWorkTime", frvDate),
      store.dispatch("data/getBeruHolidays", frvDate),
    ]);
  }

  loadedFrvMonthKey.value = monthKey;
};
</script>

<template>
  <vita-info-modal
    :open="open"
    @toggleInfoModal="emit('toggleInfoModal')"
    @open="onCreateModalOpen"
    @close="resetCreateForm"
    :modal-icon="false"
    modal-size="modal-secondary modal-beru-create"
    title="Создание потребности"
    confirm-close-title="Данные не сохранены"
    confirm-close="Вы уверены, что хотите выйти без сохранения?"
    v-slot="{ showConfirm }"
  >
    <div class="modal-content" @click.stop>
      <template v-if="isCreateModalLoading">
        <vita-loader-spinner />
      </template>
      <template v-else-if="hasLoadingError">
        <div class="empty-placeholder">
          <VitaEmptyPlaceholder
            qa-name="beru-create-error"
            icon="isOffline"
            message="Ошибка загрузки данных"
            description="При загрузке данных произошла ошибка. Попробуйте обновить страницу позже."
            icon-width="100"
            icon-height="100"
            :on-refresh-click="onCreateModalOpen"
          />
        </div>
      </template>
      <template v-else>
        <div class="create-counters">
          <div class="create-counters-month">{{ countersMonthTitle }}</div>
          <div class="create-counters-row">
            <vita-counter
              v-if="coreStaff"
              title="ФРВ"
              subtitle="Осн."
              :info="coreStaff"
              frv-type="main"
            />
            <vita-counter
              v-if="hasHolidaysFRVData"
              title="ФРВ РВ"
              :info="holidaysFRVData"
              frv-type="rv"
              :showIndicator="false"
            />
          </div>
        </div>

        <form
          class="create-form"
          autocomplete="new-password"
          @submit.prevent="onFormSubmit"
        >
          <p class="create-form-header">
            Чтобы создать потребность, заполните все обязательные поля:
          </p>

          <div class="create-form-field">
            <label class="create-form-label" data-qa="beru-create-label-reason"
              >Укажите причину<span
                class="required"
                :class="{ 'is-filled': selectedReason }"
                :data-qa="`beru-create-label-reason-status-${selectedReason ? 'filled' : 'empty'}`"
              ></span
            ></label>
            <vita-select
              v-model="selectedReason"
              :options="
                closeReasons.map((reason) => ({
                  label: reason.reason,
                  value: reason.id,
                }))
              "
              name="reason"
              placeholderEmpty="Нет доступных причин"
              placeholder="Причина"
              auto-select-first
              :disabled="showConfirm"
            />
          </div>
          <div class="create-form-field" data-qa="beru-create-field-date-block">
            <label class="create-form-label" data-qa="beru-create-label-date"
              >Дата потребности<span
                class="required"
                :class="{ 'is-filled': selectedDate }"
                :data-qa="`beru-create-label-date-status-${selectedDate ? 'filled' : 'empty'}`"
              ></span
            ></label>
            <VueDatePicker
              v-model="selectedDate"
              locale="ru"
              :format-locale="ru"
              format="dd.MM.yyyy"
              month-name-format="long"
              teleport
              :enable-time-picker="false"
              @update:model-value="handleDateChange"
              :min-date="minDate"
              :max-date="maxDate"
              auto-apply
              :clearable="false"
              prevent-min-max-navigation
              :disabled="showConfirm"
              data-qa="beru-create-datepicker-date-select"
            >
              <template #input-icon>
                <vita-icon name="calendar-grey" />
              </template>
              <template #arrow-left>
                <vita-icon name="chevron-left-calendar" />
              </template>
              <template #arrow-right>
                <vita-icon
                  style="transform: rotate(-180deg)"
                  name="chevron-left-calendar"
                />
              </template>
            </VueDatePicker>
          </div>

          <div class="create-form-field">
            <label class="create-form-label" data-qa="beru-create-label-reason-type"
              >Укажите вид причины<span
                class="required"
                :class="{ 'is-filled': selectedTypeReason }"
                :data-qa="`beru-create-label-reason-type-status-${selectedTypeReason ? 'filled' : 'empty'}`"
              ></span
            ></label>
            <vita-select
              v-model="selectedTypeReason"
              :options="
                closeTypeReasons.map((reason) => ({
                  label: reason.typeReason,
                  value: reason.id,
                }))
              "
              name="reasonType"
              placeholderEmpty="Нет доступных видов причин"
              placeholder="Вид причины"
              :disabled="showConfirm"
            />
          </div>
          <div class="create-form-field create-form-field--time">
            <div class="time-field">
              <label class="create-form-label" data-qa="beru-create-label-time-from"
                >Время открытия<span
                  class="required"
                  :class="{ 'is-filled': timeFrom }"
                  :data-qa="`beru-create-label-time-from-status-${timeFrom ? 'filled' : 'empty'}`"
                ></span
              ></label>
              <vita-input
                class="no-shadow"
                :delete="false"
                v-model="timeFrom"
                type="time"
                icon="enter"
                :disabled="showConfirm"
                @input="inputEvent"
                data-qa="beru-create-input-time-from"
              />
            </div>
            <div class="time-field">
              <label class="create-form-label" data-qa="beru-create-label-time-to"
                >Время закрытия<span
                  class="required"
                  :class="{ 'is-filled': timeTo }"
                  :data-qa="`beru-create-label-time-to-status-${timeTo ? 'filled' : 'empty'}`"
                ></span
              ></label>
              <vita-input
                class="no-shadow"
                :delete="false"
                v-model="timeTo"
                type="time"
                icon="exit"
                :disabled="showConfirm"
                @input="inputEvent"
                data-qa="beru-create-input-time-to"
              />
            </div>
          </div>

          <div class="create-form-actions">
            <vita-button
              v-if="!showConfirm"
              class="rounded no-shadow"
              color="blue"
              width="inline"
              :disabled="btnDisabled"
              button-type="submit"
              :btnLoading="submitInProgress"
              data-qa="beru-create-button-submit"
            >
              Создать потребность
            </vita-button>
          </div>
        </form>
      </template>
    </div>
  </vita-info-modal>
</template>

<style scoped lang="scss">
.modal-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 32px;
  width: 100%;
  color: #333;
  min-width: 866px;
  min-height: 410px;

  @media (max-width: 1059px) {
    min-width: auto;
  }

  p {
    margin: 0;
    font-size: 15px;
  }
}

.create-counters {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &-row {
    display: flex;
  }

  &-month {
    font-weight: 600;
    font-size: 15px;
    line-height: 100%;
    letter-spacing: 0;
  }
}

.create-form {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: auto auto auto 1fr;
  gap: 24px 32px;
  flex: 1;

  &-header {
    grid-column: 1 / -1;
    font-weight: 600;
    font-size: 14px;
    line-height: 17px;
    color: #333333;
    margin-bottom: 4px;
  }

  &-field {
    display: flex;
    flex-direction: column;
  }

  &-field--time {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  }

  &-label {
    font-weight: 500;
    font-size: 13px;
    line-height: 16px;
    color: #7d7d7d;
    margin-bottom: 8px;

    .required {
      margin-left: 2px;
      position: relative;

      &::after {
        content: "*";
        color: rgba(255, 59, 48, 1);
      }

      &.is-filled::after {
        content: "";
        transform: translateY(-2px);
        display: inline-block;
        width: 8px;
        height: 8px;
        background-size: contain;
        background-repeat: no-repeat;
        background-image: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iOCIgaGVpZ2h0PSI4IiB2aWV3Qm94PSIwIDAgOCA4IiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cGF0aCBkPSJNOCAwLjkzMDE5M0wzLjAzMzM1IDZMMCAyLjkwMzY0TDAuOTExMjY1IDEuOTczNDVMMy4wMzMzNSA0LjEzOTYxTDcuMDg4NzQgMEw4IDAuOTMwMTkzWiIgZmlsbD0iIzMwQjY0OCIvPgo8L3N2Zz4K");
      }
    }
  }

  &-actions {
    grid-column: 1 / -1;
    align-self: end;
    display: flex;
    justify-content: flex-end;
  }
}

.select-wrapper {
  position: relative;

  .select-icon {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    width: 14px;
    height: 14px;
  }
}

.create-form-label {
  color: rgba(51, 51, 51, 1);
  font-weight: 400;
  font-size: 15px;
  line-height: 1.2;
  letter-spacing: 0;
}

.time-field {
  display: flex;
  flex-direction: column;
}

.create-form-actions button {
  min-width: 325px;
}

:deep(.input-box) {
  margin-bottom: 0;
  height: 34px;
}

:deep(.loader-alt) {
  position: absolute;
  inset: 0;
  height: auto;
}

:deep(.counter:not(:last-child)) {
  margin-right: 20px;
}

:deep(.counter:not(:first-child):last-child) {
  border-right: 0;
  padding-right: 0;
  padding-left: 20px !important;
}

:deep(.input-box input:disabled) {
  background-color: #ffffff;
  border-color: rgb(229, 229, 229);
  color: rgb(229, 229, 229);
  pointer-events: none;
}

:deep(.input-box.disabled .input-box__icon) {
  opacity: 0.2;
}

:deep(.input-box.disabled) {
  pointer-events: none;
  border-color: rgb(229, 229, 229);
}

:deep(.counter__item h4) {
  @media (max-width: 1059px) {
    max-width: 50px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>
